// Theme: hell, dunkel, oder was das System sagt.
//
// Das Attribut sitzt auf <html>; die eigentlichen Farben stehen als
// CSS-Variablen in index.css. Umschalten heißt also: ein Attribut setzen.

import { useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";

const KEY = "sirges-theme";
const listeners = new Set<() => void>();

function read(): ThemeMode {
  const v = localStorage.getItem(KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function systemPrefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/** Welches Theme ist gerade tatsächlich sichtbar? */
export function resolve(mode: ThemeMode): "light" | "dark" {
  return mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode;
}

/** Muss zu --ink-950 in index.css passen — das ist die Fläche, an die die Leiste anschließt. */
const LEISTE = { dark: "#0a0b0d", light: "#f3f4f6" } as const;

function apply(mode: ThemeMode) {
  const sichtbar = resolve(mode);
  document.documentElement.dataset.theme = sichtbar;

  // Die Statusleiste gehört zur App, nicht zum Browser. Bleibt sie beim
  // Umschalten stehen, sitzt ein schwarzer Balken über einer weißen App —
  // und die Illusion ist hin.
  document.getElementById("theme-color")?.setAttribute("content", LEISTE[sichtbar]);
}

export function setTheme(mode: ThemeMode) {
  localStorage.setItem(KEY, mode);
  apply(mode);
  listeners.forEach((l) => l());
}

/**
 * Muss VOR dem ersten Render laufen (siehe main.tsx), sonst blitzt beim Start
 * kurz das falsche Theme auf.
 */
export function initTheme() {
  apply(read());
  // Systemwechsel live übernehmen, solange "system" eingestellt ist.
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (read() === "system") {
      apply("system");
      listeners.forEach((l) => l());
    }
  });
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

export function useTheme() {
  const mode = useSyncExternalStore(subscribe, read, () => "system" as ThemeMode);
  return { mode, resolved: resolve(mode), setTheme };
}
